import React, { Component } from "react";
import AddIcon from "@material-ui/icons/Add";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import EditIcon from "@material-ui/icons/Edit";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import Grow from "@material-ui/core/Grow";
import IconButton from "@material-ui/core/IconButton";
import InfoIcon from "@material-ui/icons/Info";
import Slide from "@material-ui/core/Slide";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import withStyles from "@material-ui/core/styles/withStyles";
import withWidth from "@material-ui/core/withWidth";
import { getIn, connect, FieldArray } from "formik";
import PropertyForm from "../property/PropertyForm";
import { AddPropertyFieldComponent } from "../property/PropertyField";
import { transformErrors } from "../../state/api";

const DeleteDialog = withWidth()(
  ({
    open,
    onCancel,
    width,
    label,
    item,
    content,
    cancelText = "Cancel",
    okText = "OK",
    onConfirm
  }) =>
    open && (
      <Dialog
        open={open}
        onClose={onCancel}
        scroll="paper"
        maxWidth="xl"
        fullScreen={width === "xs"}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        <DialogTitle disableTypography>
          <Typography variant="h6">{label && item && label(item)}</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {content && item && content(item)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>{cancelText}</Button>
          <Button onClick={onConfirm} autoFocus>
            {okText}
          </Button>
        </DialogActions>
      </Dialog>
    )
);

const AddEditDialog = withWidth()(
  ({
    open,
    onCancel,
    width,
    label,
    item,
    onSave,
    cancelText = "Cancel",
    okText = "OK",
    parentValues,
    ...rest
  }) => {
    if (!open) return null;

    return (
      <Dialog
        open={open}
        onClose={onCancel}
        scroll="body"
        maxWidth="xl"
        fullScreen={width === "xs"}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        <DialogTitle disableTypography>
          <Typography variant="h6">{label}</Typography>
        </DialogTitle>
        <PropertyForm
          Wrapper={DialogContent}
          ToolbarWrapper={DialogActions}
          hideCancel={false}
          hideReset={true}
          hideSave={false}
          {...{
            initialValues: { ...item, parentValues },
            onSubmit: onSave,
            onCancel: onCancel,
            cancelText,
            okText
          }}
          {...rest}
        ></PropertyForm>
      </Dialog>
    );
  }
);

const DataCard = ({
  hideEdit,
  readOnly,
  cardIcon,
  onDelete,
  FieldDeleteIcon = DeleteForeverIcon,
  onEdit,
  FieldEditIcon = EditIcon,
  title,
  subheader,
  content,
  disableTypography
}) => (
  <Card>
    <CardHeader
      avatar={cardIcon && <Avatar>{cardIcon}</Avatar>}
      action={
        !readOnly &&
        !hideEdit && (
          <IconButton aria-label="Edit" onClick={onEdit}>
            <FieldEditIcon fontSize="small" />
          </IconButton>
        )
      }
      title={title}
      subheader={subheader}
    />
    {content && (
      <CardContent>
        {disableTypography ? content : <Typography>{content}</Typography>}
      </CardContent>
    )}
    {!readOnly && (
      <CardActions>
        <IconButton onClick={onDelete} aria-label="Delete">
          <FieldDeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    )}
  </Card>
);

const DataCardGrid = ({
  hideEdit,
  readOnly,
  items,
  cardIcon,
  onDelete,
  onEdit,
  FieldDeleteIcon,
  FieldEditIcon,
  primary,
  secondary,
  content,
  disableTypography,
  name,
  values,
  rowKey = 0,
  box,
  disabled,
  hasError,
  errors
}) =>
  items && (
    <Grid container>
      {items.map((item, index) => {
        const error = hasError && transformErrors(errors[index]);
        const schemaError =
          (typeof error === "string" && error) || (error && error["_schema"]);

        return (
          <Grow in key={name + "-item-" + rowKey++}>
            <Grid item>
              <Box {...box}>
                <DataCard
                  disabled={disabled}
                  hideEdit={hideEdit}
                  readOnly={readOnly || disabled}
                  cardIcon={cardIcon && cardIcon(item, values)}
                  onDelete={onDelete && onDelete(item, index)}
                  onEdit={onEdit && onEdit(item, index)}
                  FieldDeleteIcon={FieldDeleteIcon}
                  FieldEditIcon={FieldEditIcon}
                  title={primary && primary(item, values, error)}
                  subheader={secondary && secondary(item, values, error)}
                  content={content && content(item, values, error)}
                  disableTypography={
                    disableTypography && disableTypography(item, values)
                  }
                />
              </Box>
              {schemaError && (
                <Typography align="center" color="error" variant="caption">
                  {schemaError}
                </Typography>
              )}
            </Grid>
          </Grow>
        );
      })}
    </Grid>
  );

const addRowToolbarStyles = theme => ({
  leftIcon: {
    marginRight: theme.spacing(1)
  }
});

const AddRowButton = withStyles(addRowToolbarStyles, {
  name: "LcCardFieldAddRowButton"
})(({ classes, onClick, label, FieldAddIcon, variant, disabled }) => (
  <Toolbar>
    <Button variant={variant} onClick={onClick} disabled={disabled}>
      <FieldAddIcon fontSize="small" className={classes.leftIcon} />
      {label}
    </Button>
  </Toolbar>
));

const resolved = () => Promise.resolve();

const InnerCardField = connect(
  class CardField extends Component {
    static defaultProps = {
      onSaveEdit: resolved,
      onConfirmDelete: resolved,
      onCancelEdit: resolved
    };

    state = {
      deleteDialogOpen: false,
      editDialogOpen: false
    };

    onDelete = (item, index) => () => {
      this.setState({
        deleteDialogOpen: true,
        deleteItem: item,
        deleteItemIndex: index
      });
    };

    onCancelDelete = () => {
      this.setState({ deleteDialogOpen: false });
    };

    onAdd = () => {
      this.setState({
        editDialogOpen: true,
        editItem: this.props.defaultValues,
        editItemIndex: -1
      });
    };

    onEdit = (item, index) => () => {
      this.setState({
        editDialogOpen: true,
        editItem: item,
        editItemIndex: index
      });
    };

    onCancelEdit = () => {
      this.setState({ editDialogOpen: false });
    };

    onConfirmDelete = arrayHelpers => () => {
      this.props.onConfirmDelete(this.state).then(() => {
        this.setState({ deleteDialogOpen: false }, () => {
          arrayHelpers.remove(this.state.deleteItemIndex);
        });
      });
    };

    onSaveEdit = arrayHelpers => (vals, form) => {
      this.props.onSaveEdit(vals, form).then(() => {
        if (this.state.editItemIndex === -1) {
          arrayHelpers.push(vals);
        } else {
          arrayHelpers.replace(this.state.editItemIndex, vals);
        }
        this.setState({ editDialogOpen: false });
      }).catch(err => {
        //form.setErrors(...);
        form.setSubmitting(false);
      });
    };

    render = () => {
      const {
        formik: { values, touched, errors, submitCount },
        name,
        add: { icon: FieldAddIcon = AddIcon, label: addLabel = "Add" } = {},
        delete: {
          icon: FieldDeleteIcon = DeleteForeverIcon,
          label: deleteLabel = () => "Delete",
          content: deleteContent,
          cancel: deleteCancel,
          ok: deleteOk
        } = {},
        edit: {
          icon: FieldEditIcon = EditIcon,
          label: editLabel = "Edit",
          cancel: editCancel,
          ok: editOk
        } = {},
        card: {
          primary,
          secondary,
          content,
          icon: cardIcon = () => <InfoIcon />,
          box,
          disableTypography
        } = {},
        max = -1,
        section = {
          paddingBottom: 0
        },
        readOnly,
        arrayHelpers,
        required,
        hideEdit,
        ...rest
      } = this.props;
      const items = getIn(values, name) || [];

      const fieldTouched = submitCount > 0 || getIn(touched, name);
      const error = getIn(errors, name);
      const hasError = Boolean(fieldTouched && error);
      const isDeepError = hasError && typeof error !== "string";

      return (
        <>
          <DataCardGrid
            disabled={this.props.disabled}
            readOnly={readOnly}
            hideEdit={hideEdit}
            items={items}
            cardIcon={cardIcon}
            onDelete={this.onDelete}
            onEdit={this.onEdit}
            FieldDeleteIcon={FieldDeleteIcon}
            FieldEditIcon={FieldEditIcon}
            primary={primary}
            secondary={secondary}
            content={content}
            name={name}
            values={values}
            box={box}
            disableTypography={disableTypography}
            hasError={isDeepError}
            errors={error}
          />
          <FormControl
            error={hasError}
            required={required}
            disabled={this.props.disabled}
          >
            {hasError && typeof error === "string" && (
              <FormHelperText>{error}</FormHelperText>
            )}
          </FormControl>

          {!readOnly && (
            <>
              {(max === -1 || max > items.length) && (
                <AddRowButton
                  onClick={this.onAdd}
                  label={addLabel}
                  FieldAddIcon={FieldAddIcon}
                  disabled={this.props.disabled}
                />
              )}

              <DeleteDialog
                open={this.state.deleteDialogOpen}
                onCancel={this.onCancelDelete}
                label={deleteLabel}
                item={this.state.deleteItem}
                content={deleteContent}
                cancelText={deleteCancel}
                okText={deleteOk}
                onConfirm={this.onConfirmDelete(arrayHelpers)}
              />

              <AddEditDialog
                open={this.state.editDialogOpen}
                onCancel={this.onCancelEdit}
                label={this.state.editItemIndex === -1 ? addLabel : editLabel}
                item={this.state.editItem}
                onSave={this.onSaveEdit(arrayHelpers)}
                cancelText={editCancel}
                okText={editOk}
                section={section}
                parentValues={values}
                {...rest}
              />
            </>
          )}
        </>
      );
    };
  }
);

const CardField = props => (
  <FieldArray
    name={props.name}
    render={arrayHelpers => (
      <InnerCardField {...props} arrayHelpers={arrayHelpers} />
    )}
  />
);

AddPropertyFieldComponent("card", CardField);

export default CardField;
